/**
 * Subscribe / Unsubscribe for a report's daily email, on the generic
 * report-subscription surface (one person may hold several subscriptions saved
 * with different filters). When the caller already holds one saved with exactly
 * these filters the same button removes it. Renders disabled, never hidden, when
 * the subscriptions service is unavailable.
 *
 * Shared by the Reports pages that have a daily email (Unposted Activity,
 * Loyalty). The page decides which filters a subscription saves and passes them
 * in as the flat map the api keeps.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2, Mail, MailCheck } from 'lucide-react';

import { itemSalesFiltersKey } from '@/lib/useItemSales';
import {
  useCreateReportSubscription,
  useDeleteReportSubscription,
  useReportSubscriptions,
} from '@/lib/useReportSubscriptions';

/** The small bordered button every Reports page toolbar uses. */
export const reportToolbarBtnCls =
  'inline-flex items-center gap-1 rounded border border-amp-line px-3 py-1.5 text-xs text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm';

export function ReportSubscribeControl({
  reportType,
  filters,
  subscribeHint,
}: {
  reportType: string;
  /** What a new subscription saves (a flat map of scalars). */
  filters: Record<string, unknown>;
  /** Tooltip for the Subscribe button: what the email contains and which filters it saves. */
  subscribeHint: string;
}) {
  const { subscriptions, available } = useReportSubscriptions();
  const createMut = useCreateReportSubscription();
  const deleteMut = useDeleteReportSubscription();
  const [feedback, setFeedback] = useState<{ kind: 'saved' | 'error'; msg: string } | null>(null);

  useEffect(() => {
    if (!feedback) return;
    const t = window.setTimeout(() => setFeedback(null), 4000);
    return () => window.clearTimeout(t);
  }, [feedback]);

  // The same canonical comparison Item Sales uses: key and list order do not matter.
  const existing = useMemo(() => {
    const key = itemSalesFiltersKey(filters);
    return (
      subscriptions.find(
        (s) => s.report_type === reportType && s.status === 'active' && itemSalesFiltersKey(s.filters) === key,
      ) ?? null
    );
  }, [subscriptions, filters, reportType]);

  if (!available) {
    return (
      <button
        type="button"
        disabled
        title="The daily email is unavailable right now, so you cannot subscribe. Try again shortly; if it persists, tell IT."
        className={reportToolbarBtnCls}
      >
        <Mail className="h-3 w-3" aria-hidden />
        Subscribe
      </button>
    );
  }

  const isBusy = createMut.isPending || deleteMut.isPending;
  const subscribed = existing !== null;

  const handleClick = () => {
    if (isBusy) return;
    if (existing) {
      deleteMut.mutate(existing.id, {
        onSuccess: () => setFeedback({ kind: 'saved', msg: 'Unsubscribed' }),
        onError: (e) => setFeedback({ kind: 'error', msg: e instanceof Error ? e.message : 'Could not unsubscribe' }),
      });
      return;
    }
    createMut.mutate(
      { reportType, filters },
      {
        onSuccess: () => setFeedback({ kind: 'saved', msg: 'Subscribed. Manage it in Settings.' }),
        onError: (e) => setFeedback({ kind: 'error', msg: e instanceof Error ? e.message : 'Could not subscribe' }),
      },
    );
  };

  const Icon = isBusy ? Loader2 : subscribed ? MailCheck : Mail;
  return (
    <div className="flex items-center gap-2">
      {feedback && (
        <span
          role="status"
          className={
            feedback.kind === 'error'
              ? 'text-[12px] font-semibold text-red-700 dark:text-red-300'
              : 'inline-flex items-center gap-1 text-[12px] font-semibold text-amp-ink dark:text-amp-dark-ink'
          }
        >
          {feedback.kind === 'saved' && <Check className="h-3 w-3" aria-hidden />}
          {feedback.msg}
        </span>
      )}
      <Link
        to="/settings"
        className="text-[12px] text-amp-ink-muted underline hover:text-amp-ink hover:no-underline dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
      >
        Manage
      </Link>
      <button
        type="button"
        onClick={handleClick}
        disabled={isBusy}
        title={subscribed ? 'You get this report by email every morning with the filters saved on it. Click to stop.' : subscribeHint}
        className={reportToolbarBtnCls}
      >
        <Icon className={`h-3 w-3 ${isBusy ? 'animate-spin' : ''}`} aria-hidden />
        {isBusy ? (subscribed ? 'Unsubscribing...' : 'Subscribing...') : subscribed ? 'Unsubscribe' : 'Subscribe'}
      </button>
    </div>
  );
}
